import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import React, { useContext, useEffect, useRef, useState } from "react";

import Icon from "../assets/avatar.svg";
import { CommentsContext } from "../context";
import { CARD_WIDTH } from "../conts";
import Button, { ButtonType } from "../../Button/Button";
import { Icon24Check, Icon24Delete } from "../../Icons";
import InputChat from "../../InputChat/InputChat";
import { useOutsideEvent } from "../../../hooks";

interface IProps {
  onClick: () => void;
  id: string;
  name: string;
  color?: string;
  isMain: boolean;
  isNew?: boolean;
  isHidden?: boolean;
  isGlobal?: boolean;
  isResolved?: boolean;
  isShowed?: boolean;
  isHovered?: boolean;
  isFocused?: boolean;
  isEditable?: boolean;
  isDraft?: boolean;
  from: "manager" | "client";
  text: string;
  position: number;
  cardsCount: number;
  className?: string;
  onResolve: (state: boolean) => void;
  toggleActive?: (isActive: boolean) => void;
  replyId?: string;
  allManagerCommentsResolved?: boolean;
}

function Card(props: IProps) {
  const ctx = useContext(CommentsContext);
  const [isResolved, setIsResolved] = useState(props.isResolved);
  useEffect(() => {
    setIsResolved(props.isResolved);
  }, [props.isResolved]);
  const [isEditable, setIsEditable] = useState(props.isEditable);
  const [maxHeight, setMaxHeight] = useState("212px");
  const [height, setHeight] = useState("auto");
  const [counter, setCounter] = useState(0);
  const [top, setTop] = useState(0);
  const { isMain } = props;
  const cardRef = useRef<HTMLDivElement>(null);

  function toggleActive(state: boolean) {
    props.toggleActive && props.toggleActive(state);
  }

  useEffect(() => {
    setTimeout(() => {
      setMaxHeight(isEditable || props.isShowed ? "auto" : "212px");
    }, 350);
  }, [props.isShowed, isEditable]);

  useEffect(() => {
    setIsEditable(props.isEditable);
  }, [props.isEditable]);

  useEffect(() => {
    setCounter(props.cardsCount - 1 - props.position);
  }, [props]);

  useEffect(() => {
    if (isMain || props.isShowed) {
      setHeight("auto");
    } else if (props.isHovered) {
      setHeight("64px");
    } else {
      setHeight("32px");
    }
  }, [isMain, props]);

  useEffect(() => {
    if (isMain || props.isShowed) {
      setTop(0);
    } else {
      setTop(-counter * 24);
    }
  }, [counter, props, isMain]);

  useOutsideEvent(cardRef, () => {
    if (props.isNew) {
      ctx.deleteComment?.(props.id);
    } else if (isEditable) {
      setIsEditable(false);
    }
  });

  // Check if there is a response from the opposite side
  function hasReplyFromOtherSide(
    comments: any[],
    targetId: string,
    otherSide: "manager" | "client"
  ): boolean {
    for (const comment of comments) {
      if (comment.replyId === targetId && comment.from === otherSide)
        return true;
      if (comment.replies?.length) {
        if (hasReplyFromOtherSide(comment.replies, targetId, otherSide))
          return true;
      }
    }
    return false;
  }

  const sameThread = ctx.threads[props.name] ?? [];
  const isOwnComment = props.from === (ctx.isManager ? "manager" : "client");
  const otherSide: "manager" | "client" = ctx.isManager ? "client" : "manager";

  const hasRepliesFromOtherSide = hasReplyFromOtherSide(
    sameThread,
    props.id,
    otherSide
  );

  const isFromOtherSide = props.from !== (ctx.isManager ? "manager" : "client");

  // Right to edit/delete
  const canEditOrDelete =
    isOwnComment && !hasRepliesFromOtherSide && !isResolved;

  // Right to show Reply button
  const canShowReplyButton =
    props.isShowed &&
    !ctx.hidden &&
    isFromOtherSide &&
    !hasReplyFromOtherSide(
      sameThread,
      props.id,
      ctx.isManager ? "manager" : "client"
    );

  return (
    <div
      ref={cardRef}
      onClick={() => props.onClick()}
      onMouseEnter={() => toggleActive(true)}
      onMouseLeave={() => toggleActive(false)}
      className={clsxm(
        props.className ?? "",
        props.isHidden && !props.isShowed && "hidden",
        props.isShowed ? "transition-[top]" : "transition-all",
        props.isFocused ? `bg-${props.color ?? "gray"}-600` : "bg-white-900",
        "relative h-auto rounded-2xl py-4 pr-4 text-base shadow-xl duration-500 ease-in-out"
      )}
      style={{
        width: props.isGlobal
          ? "100%"
          : `${
              props.isShowed
                ? CARD_WIDTH
                : Math.max(0, CARD_WIDTH - counter * 32)
            }px`,
        height,
        top: `${top}px`,
        zIndex: props.position,
        left: `${props.isShowed ? 0 : counter * 16}px`,
      }}
    >
      <div
        className="flex h-full flex-row overflow-hidden"
        style={{ maxHeight, transition: "max-height 0.45s ease-in" }}
        key={maxHeight + props.id}
        data-ismain={isMain}
      >
        <div
          className={clsxm(
            "mr-4",
            props.isFocused ? "border-l-2 border-transparent" : "border-l-2",
            props.color ? `border-${props.color}-600` : "border-gray-50a"
          )}
        ></div>
        <div className="w-full overflow-hidden">
          <div
            className={clsxm(
              "mb-3 flex",
              isMain || props.isShowed ? "opacity-1" : "hidden"
            )}
          >
            <span>
              <Icon width="32px" height="32px" />
            </span>
            <span className="ml-2 w-full border-gray-200 pt-1 text-base font-semibold">
              {props.from === "manager" ? "Manager" : "Client"}
              {!props.isShowed && props.from === "manager" && (
                <span className="ml-2 text-mdb text-black-600">
                  {props.allManagerCommentsResolved
                    ? "All comments are resolved"
                    : "Comments need to be resolved"}
                </span>
              )}
            </span>
            {canEditOrDelete && (
              <>
                <span>
                  <Button
                    type={ButtonType.Secondary}
                    size="200"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setIsEditable(true);
                      ctx.editComment && ctx.editComment(props.id);
                    }}
                  >
                    Edit
                  </Button>
                </span>
                <span className="ml-3">
                  <Button
                    type={ButtonType.Secondary}
                    icon={<Icon24Delete />}
                    size="200"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      ctx.deleteComment && ctx.deleteComment(props.id);
                    }}
                  />
                </span>
              </>
            )}

            {/* Кнопка Resolve/Unresolve - для клиента на админских комментах и для админа на клиентских комментах */}
            {((!ctx.isManager && props.from === "manager" && props.isShowed) ||
              (ctx.isManager && props.from === "client" && props.isShowed)) && (
              <span className="whitespace-nowrap">
                <Button
                  type={ButtonType.Secondary}
                  icon={isResolved ? "" : <Icon24Check />}
                  size="200"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const newResolvedState = !isResolved;
                    setIsResolved(newResolvedState);
                    props.onResolve(newResolvedState);
                  }}
                >
                  {isResolved ? "Unresolve" : "I resolved it"}
                </Button>
              </span>
            )}

            {canShowReplyButton && (
              <span className="ml-3">
                <Button
                  type={ButtonType.Secondary}
                  size="200"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    ctx.addComment?.(props.name, undefined, props.id);
                  }}
                >
                  Reply
                </Button>
              </span>
            )}
          </div>
          {isEditable ? (
            <div>
              <InputChat
                onSave={(value) =>
                  ctx.saveComment && ctx.saveComment(props.id, value)
                }
                value={props.text}
                placeholder=""
                autoFocus
              />
            </div>
          ) : (
            <div
              onClick={() => props.onClick()}
              className={clsxm(
                "overflow-hidden break-words",
                !props.isShowed && !props.isHovered && !isMain && "opacity-0",
                !isMain &&
                  !props.isShowed &&
                  props.isHovered &&
                  "h-6 text-ellipsis transition-opacity duration-500 ease-in-out"
              )}
              dangerouslySetInnerHTML={{
                __html: props.text.replace(/\n/g, "<br/>") ?? "",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Card;
